import { Button, Col, Popover, Row, Space } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import './DatePicker.less';
import { format } from 'date-fns';
import { ReactComponent as DownArrow } from '../../../../assets/images/svg/down-arrow.svg';
interface TimePickerDropdownProps {
  onTimeSelect?: any;
  initialTime?: string;
  onSelectNow?: any;
  showNow?: boolean;
  isAdmitDateSelected?: boolean;
  isPreventTimeSelection?: boolean;
}

// tslint:disable-next-line: typedef
const TimePickerDropdown: React.FC<TimePickerDropdownProps> =  ({
  onTimeSelect,
  initialTime,
  onSelectNow,
  showNow,
  isAdmitDateSelected,
  isPreventTimeSelection,
}) => {
  const getCurrentTime = (): any => {
    const now = new Date();
    return format(now, 'hh:mm aa');
  };

  const defaultTime = initialTime || getCurrentTime();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(parseInt(defaultTime.split(':')[0], 10));
  const [selectedMinute, setSelectedMinute] = useState(parseInt(defaultTime.split(':')[1], 10));
  const [selectedMeridiem, setSelectedMeridiem] = useState(defaultTime.split(' ')[1]);
  const [tempHour, setTempHour] = useState(selectedHour);
  const [tempMinute, setTempMinute] = useState(selectedMinute);
  const [tempMeridiem, setTempMeridiem] = useState(selectedMeridiem);
  const preventOpenTimePicker = (!showNow && (!isAdmitDateSelected || isPreventTimeSelection));

  const hours = Array.from({ length: 12 }, (_: any, i: any): any => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_: any, i: any): any => i.toString().padStart(2, '0'));
  const meridians = ['AM', 'PM'];

  const minuteColumnRef = useRef<HTMLDivElement>(null);

  useEffect((): void => {
    setTempHour(selectedHour);
    setTempMinute(selectedMinute);
    setTempMeridiem(selectedMeridiem);
  }, [selectedHour, selectedMinute, selectedMeridiem]);

  useEffect((): void => {
    if (isOpen) {
      scrollToSelectedMinute();
    }
  }, [isOpen, selectedMinute]);

  const handleHourChange = (event: React.MouseEvent<HTMLOptionElement>): void => {
    setTempHour(parseInt((event?.currentTarget as HTMLOptionElement)?.value, 10));
  };

  const handleMinuteChange = (event: React.MouseEvent<HTMLOptionElement>): void => {
    setTempMinute(parseInt((event?.currentTarget as HTMLOptionElement)?.value, 10));
  };

  const handleMeridiemChange = (event: React.MouseEvent<HTMLOptionElement>): void => {
    setTempMeridiem(event?.currentTarget?.value);
  };

  const handleToggleDropdown = (): void => {
    if (preventOpenTimePicker) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleOk = (): void => {
    setSelectedHour(tempHour);
    setSelectedMinute(tempMinute);
    setSelectedMeridiem(tempMeridiem);
    setIsOpen(false);
    onTimeSelect(formattedTime, { hour: tempHour, minute: tempMinute, meridiem: tempMeridiem });
  };

  const handleCancel = (): void => {
    setTempHour(selectedHour);
    setTempMinute(selectedMinute);
    setTempMeridiem(selectedMeridiem);
    setIsOpen(false);
    onTimeSelect(`${selectedHour?.toString().padStart(2, '0')}:${selectedMinute?.toString().padStart(2, '0')} ${selectedMeridiem}`,
      { hour: selectedHour, minute: selectedMinute, meridiem: selectedMeridiem });
  };

  const formattedTime = `${tempHour?.toString().padStart(2, '0')}:${tempMinute?.toString().padStart(2, '0')} ${tempMeridiem}`;

  const handleNow = (): void => {
    if (!showNow) {
      return;
    }
    const now = new Date();
    const formattedNow = format(now, 'hh:mm aa');
    const [time, meridiem] = formattedNow?.split(' ');
    const [hour, minute] = time?.split(':');
    const hourInt = parseInt(hour, 10);
    const minuteInt = parseInt(minute, 10);

    setSelectedHour(hourInt);
    setSelectedMinute(minuteInt);
    setSelectedMeridiem(meridiem);
    setTempHour(hourInt);
    setTempMinute(minuteInt);
    setTempMeridiem(meridiem);

    onSelectNow(now);
    setIsOpen(false);
  };

  const scrollToSelectedMinute = (): void => {
    if (minuteColumnRef.current) {
      const selectedOption = minuteColumnRef.current.querySelector(`.selectedTime`);
      if (selectedOption) {
        selectedOption.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }
  };

  const TimeDisplay = ({ hour, minute, meridiem }: any): JSX.Element => (
    <div className="timeSelectedContent">
      <span>{hour?.toString().padStart(2, '0')}</span>:
      <span>{minute?.toString().padStart(2, '0')}</span>{' '}
      <span>{meridiem}</span>
      <DownArrow className="time-selector-dropdown" />
    </div>
  );

  return (
    <Row className="time-picker-dropdown">
      {preventOpenTimePicker ? (
        <Popover
          content= {isPreventTimeSelection ? 'Please choose the Date of Discharge first to proceed.' :
            'Please choose the Date of Admission first to proceed.'}
          overlayClassName="datepicker-diabled-btns-tooltip"
          placement="top"
        >
          <Col className="disabledNow" onClick={handleToggleDropdown}>
            <TimeDisplay
              hour={selectedHour}
              minute={selectedMinute}
              meridiem={selectedMeridiem}
            />
          </Col>
        </Popover>
      ) : (
        <Col className="timePicker-pointer" onClick={handleToggleDropdown}>
          <TimeDisplay
            hour={selectedHour}
            minute={selectedMinute}
            meridiem={selectedMeridiem}
          />
        </Col>
      )}
      {isOpen && (
        <div className="dropdown-wrapper">
          <Row className="dropdown-content">
            <Col className="dropdown-column border-line">
              {hours?.map((hour: string): any => (
                <option
                  key={hour}
                  value={hour}
                  onClick={handleHourChange}
                  className={parseInt(hour, 10) === tempHour ? 'selectedTime timepicker-content' : 'timepicker-content'}
                >
                  {hour}
                </option>
              ))}
            </Col>
            <Col className="dropdown-column scrollable" ref={minuteColumnRef}>
              {minutes?.map((minute: string): any => (
                <option
                  key={minute}
                  value={minute}
                  onClick={handleMinuteChange}
                  className={parseInt(minute, 10) === tempMinute ? 'selectedTime timepicker-content' : 'timepicker-content'}
                >
                  {minute}
                </option>
              ))}
            </Col>
            <Col className="dropdown-column">
              {meridians?.map((meridiem: string): any => (
                <option
                  key={meridiem}
                  value={meridiem}
                  onClick={handleMeridiemChange}
                  className={meridiem === tempMeridiem ? 'selectedTime timepicker-content' : 'timepicker-content'}
                >
                  {meridiem}
                </option>
              ))}
            </Col>
          </Row>
          <Row className="dropdown-actions">
            <Space>
              <Button className="timepicker-cancel-btn" onClick={handleCancel}>Cancel</Button>
              <Button className="timepicker-OK" onClick={handleOk}>OK</Button>
            </Space>
          </Row>
        </div>
      )}
      <Col>
        {!showNow ? (
          <Popover
            content="The Present Day is outside the policy period. Choose the date manually"
            overlayClassName="datepicker-diabled-btns-tooltip"
            placement="top"
          >
            <Button className="nowButton disabledNow" onClick={handleNow}>
              Now
            </Button>
          </Popover>
        ) : (
          <Button className="nowButton" onClick={handleNow}>
            Now
          </Button>
        )}
      </Col>
    </Row>
  );
};

export default TimePickerDropdown;
